// EPSS (Exploit Prediction Scoring System) helpers.
//
// EPSS reports two probabilities, each in the [0,1] range:
//   - score:      probability the vulnerability will be exploited in the wild.
//   - percentile: the vulnerability's relative rank among all scored CVEs.
//
// The UI presents `score` as an exploitation-risk heat indicator (neutral → red
// as it approaches 1) and `percentile` as a neutral ranking value. These helpers
// keep that logic pure and testable, mirroring `cvss.ts`.
//
// EPSS is a separate signal from CVSS, not a replacement: CVSS says how bad an
// exploit would be, EPSS how likely one is. They are shown side by side.

export type EpssBand = 'critical' | 'high' | 'medium' | 'low' | 'none'

/**
 * The EPSS keys as the API sends them — FLAT on the vulnerability payload, not
 * nested under an `epss` object. `epss_cve` is detail-only.
 */
export interface EpssFields {
  epss?: number | string | null
  epss_percentile?: number | string | null
  epss_cve?: string | null
}

/** A payload's EPSS keys, normalized for rendering. */
export interface EpssReading {
  /** Exploitation probability in [0,1], or `null` for "not scored". */
  score: number | null
  /** Rank among scored CVEs in [0,1], or `null` for "not scored". */
  percentile: number | null
  /** The CVE the two numbers belong to; `null` on list rows and unscored findings. */
  cve: string | null
}

/**
 * Clamp a raw probability into [0,1]. Returns `null` for absent or non-finite
 * values so callers can skip rendering. Values outside 0-1 are safely clamped
 * (e.g. a stray `1.4` becomes `1`, a negative becomes `0`).
 */
export function normalizeProbability(value: unknown): number | null {
  // Guard values that `Number()` would silently coerce to 0 (null, '', whitespace).
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return null
  return Math.min(1, Math.max(0, n))
}

/**
 * Bucket an EPSS score into a severity band. Closer to 1 = higher exploitation
 * probability = more intense band, so `0.97` reads as "critical" at a glance.
 */
export function epssBand(score: number | null): EpssBand {
  if (score == null) return 'none'
  if (score >= 0.9) return 'critical'
  if (score >= 0.5) return 'high'
  if (score >= 0.1) return 'medium'
  return 'low'
}

/** Metronic contextual colour for a band (neutral/green → red as intensity rises). */
export function epssBandColor(band: EpssBand): string {
  switch (band) {
    case 'critical':
      return 'danger'
    case 'high':
      return 'warning'
    case 'medium':
      return 'info'
    case 'low':
      return 'success'
    default:
      return 'secondary'
  }
}

/** i18n message id for a band's label; empty string for the "none" band. */
export function epssBandLabelId(band: EpssBand): string {
  switch (band) {
    case 'critical':
      return 'Critical'
    case 'high':
      return 'High'
    case 'medium':
      return 'Medium'
    case 'low':
      return 'Low'
    default:
      return ''
  }
}

/**
 * Format a 0-1 probability as a percentage string, trimming trailing zeros.
 * Tiny values keep extra precision so they don't collapse to "0%".
 *   0.97  → "97%"      0.999 → "99.9%"     0.00042 → "0.04%"
 */
export function formatProbabilityPercent(value: number | null, maxDecimals = 1): string {
  if (value == null) return '—'
  const pct = value * 100
  const decimals = pct > 0 && pct < 1 ? Math.max(maxDecimals, 2) : maxDecimals
  return `${Number(pct.toFixed(decimals))}%`
}

/**
 * Pull the EPSS keys off a vulnerability payload (list row or detail).
 *
 * Exists so no caller has to reach for `epss` directly and reinvent the
 * null-vs-zero rule: `null` means "no score available" — no CVE, a CVE EPSS
 * does not score, the async scoring task has not run yet, or an air-gapped
 * install — while `0` is a legitimate score. A truthiness check (`!epss`)
 * conflates the two and silently hides a real `0.00000`.
 */
export function readEpss(source?: EpssFields | null): EpssReading {
  const cve = typeof source?.epss_cve === 'string' ? source.epss_cve.trim() : ''
  return {
    score: normalizeProbability(source?.epss),
    percentile: normalizeProbability(source?.epss_percentile),
    cve: cve || null,
  }
}

/**
 * Whether a payload carries anything EPSS worth rendering. A `epss_cve` with no
 * numbers is not a score, so it alone does not open the section.
 */
export function hasEpssData(source?: EpssFields | null): boolean {
  const {score, percentile} = readEpss(source)
  return score != null || percentile != null
}
